import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import logger from '../../../utils/logger';
import './AdminMarketing.css';
import { fmtDate, fmtDateTime } from '../../../utils/ruDates';

const GROUPS = [
    { key: 'channel', label: 'Канал' },
    { key: 'source', label: 'utm_source' },
    { key: 'medium', label: 'utm_medium' },
    { key: 'campaign', label: 'Кампания' },
    { key: 'landing', label: 'Посадочная' },
];

function isoDaysAgo(days) {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split('T')[0];
}

function fmtMoney(value) {
    return Number(value || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
}

function AdminMarketingSources() {
    const { request } = useApi();
    const [dateFrom, setDateFrom] = useState(() => isoDaysAgo(30));
    const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
    const [groupBy, setGroupBy] = useState('channel');
    const [report, setReport] = useState(null);
    const [signups, setSignups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, group_by: groupBy });
            const data = await request(`/api/v1/admin/marketing/acquisition?${params.toString()}`);
            setReport(data);

            const signupParams = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, limit: '100' });
            const recent = await request(`/api/v1/admin/marketing/acquisition/signups?${signupParams.toString()}`);
            setSignups(recent?.items || []);
        } catch (err) {
            logger.error('Не удалось загрузить отчёт по источникам', err);
            setError('Не удалось загрузить отчёт');
            setReport(null);
        } finally {
            setLoading(false);
        }
    }, [request, dateFrom, dateTo, groupBy]);

    useEffect(() => {
        load();
    }, [load]);

    const totals = report?.totals || { signups: 0, verified: 0, paying: 0, revenue: 0 };

    return (
        <div className="mkt-page">
            <h1>Источники регистраций</h1>
            <p className="mkt-page__hint">
                Считается по данным, собранным при регистрации: utm-метки, реферер и код короткой ссылки.
                Пользователи, зарегистрированные до подключения атрибуции, в отчёт не попадают.
            </p>

            <div className="mkt-card">
                <div className="mkt-actions" style={{ marginTop: 0 }}>
                    <label className="mkt-field">
                        <span>С</span>
                        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    </label>
                    <label className="mkt-field">
                        <span>По</span>
                        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                    </label>
                    <button type="button" onClick={load} disabled={loading}>Применить</button>
                </div>

                <div className="mkt-tabs" style={{ marginTop: 14, marginBottom: 0 }}>
                    {GROUPS.map((group) => (
                        <button
                            key={group.key}
                            type="button"
                            className={`mkt-tab ${groupBy === group.key ? 'active' : ''}`}
                            onClick={() => setGroupBy(group.key)}
                        >
                            {group.label}
                        </button>
                    ))}
                </div>
            </div>

            {error && <div className="mkt-message mkt-message--error">{error}</div>}

            <div className="mkt-kpis">
                <div className="mkt-kpi"><b>{totals.signups}</b><span>регистраций</span></div>
                <div className="mkt-kpi"><b>{totals.verified}</b><span>подтвердили email</span></div>
                <div className="mkt-kpi"><b>{totals.paying}</b><span>платящих</span></div>
                <div className="mkt-kpi"><b>{fmtMoney(totals.revenue)} ₽</b><span>пополнений</span></div>
            </div>

            <div className="mkt-card">
                <div className="mkt-card__title">Воронка по каналам</div>
                {!report || report.items.length === 0 ? (
                    <div className="mkt-empty">{loading ? 'Загрузка…' : 'За период нет данных'}</div>
                ) : (
                    <table className="mkt-table">
                        <thead>
                            <tr>
                                <th>{GROUPS.find((g) => g.key === groupBy)?.label}</th>
                                <th className="num">Регистраций</th>
                                <th className="num">Подтвердили email</th>
                                <th className="num">С компанией</th>
                                <th className="num">Платящих</th>
                                <th className="num">Пополнения, ₽</th>
                                <th className="num">В платящих, %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.items.map((item) => (
                                <tr key={item.key}>
                                    <td className="mkt-code">{item.key}</td>
                                    <td className="num">{item.signups}</td>
                                    <td className="num">{item.verified}</td>
                                    <td className="num">{item.with_company}</td>
                                    <td className="num">{item.paying}</td>
                                    <td className="num">{fmtMoney(item.revenue)}</td>
                                    <td className="num">{item.conversion_to_paying}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {report?.timeline?.length > 0 && (
                <div className="mkt-card">
                    <div className="mkt-card__title">По дням</div>
                    <table className="mkt-table">
                        <thead>
                            <tr>
                                <th>Дата</th>
                                <th className="num">Переходы по ссылкам</th>
                                <th className="num">Регистрации</th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.timeline.map((row) => (
                                <tr key={row.date}>
                                    <td>{fmtDate(row.date)}</td>
                                    <td className="num">{row.clicks}</td>
                                    <td className="num">{row.signups}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="mkt-card">
                <div className="mkt-card__title">Последние регистрации</div>
                {signups.length === 0 ? (
                    <div className="mkt-empty">Нет регистраций за период</div>
                ) : (
                    <table className="mkt-table">
                        <thead>
                            <tr>
                                <th>Дата</th>
                                <th>Email</th>
                                <th>Канал</th>
                                <th>Кампания</th>
                                <th>Ссылка</th>
                                <th>Посадочная</th>
                                <th>Email подтверждён</th>
                            </tr>
                        </thead>
                        <tbody>
                            {signups.map((item) => (
                                <tr key={item.user_id}>
                                    <td>{item.created_at ? fmtDateTime(item.created_at) : '—'}</td>
                                    <td>{item.email}</td>
                                    <td className="mkt-code">{item.channel}</td>
                                    <td>{item.utm_campaign || '—'}</td>
                                    <td>{item.link_name || (item.link_code ? item.link_code : '—')}</td>
                                    <td className="mkt-code">{item.landing_path || '—'}</td>
                                    <td>{item.email_verified ? 'да' : 'нет'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default AdminMarketingSources;
